/**
 * Config-driven table for the flat HR entities (locations, branches, etc).
 *
 * Owns the full list/create/edit/delete loop so each section page is just
 * a config + initial data fetch.
 *
 * Props:
 *   title          string
 *   fetcher        () => Promise<{items, total}>
 *   columns        [{ key, label, render? }]   render(row) => ReactNode
 *   formFields     EntityFormModal field config
 *   onCreate       (body) => Promise
 *   onUpdate       (id, body) => Promise
 *   onDelete       (id) => Promise
 *   canWrite       boolean      (hides Add/Edit/Delete when false)
 *   emptyMessage   string
 *   actions        optional [{ label, onClick(row) }]   per-row extra actions
 *   searchKeys     optional [string]   row field names to match against; enables a search box
 *   searchPlaceholder  string
 *
 * Reload is automatic after create/update/delete.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import EntityFormModal from './EntityFormModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';

export default function EntityTable({
  title,
  fetcher,
  columns,
  formFields,
  onCreate,
  onUpdate,
  onDelete,
  canWrite = true,
  emptyMessage = 'Nothing here yet.',
  actions = [],
  onRowsLoaded,
  deleteLabel = 'Delete',
  deleteConfirmTitle,
  deleteConfirmMessage = 'This is permanent and cannot be undone.',
  searchKeys,
  searchPlaceholder = 'Search…',
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create mode
  const [deleteTarget, setDeleteTarget] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetcher();
      const items = res.items || [];
      setRows(items);
      onRowsLoaded?.(items);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || 'Failed to load',
      );
    } finally {
      setLoading(false);
    }
  }, [fetcher]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setModalOpen(true);
  };

  const handleSubmit = async (body) => {
    if (editing) {
      await onUpdate(editing.id, body);
    } else {
      await onCreate(body);
    }
    await reload();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await onDelete(deleteTarget.id);
    setDeleteTarget(null);
    await reload();
  };

  const visibleRows = useMemo(() => {
    if (!searchKeys || !query.trim()) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter((row) =>
      searchKeys.some((k) => String(row[k] ?? '').toLowerCase().includes(q)),
    );
  }, [rows, searchKeys, query]);

  return (
    <section className="entity-section">
      <div className="entity-head">
        <h2 className="page-title-sm">{title}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {searchKeys && (
            <div className="tl-search-wrap">
              <Search className="tl-search-icon" size={14} />
              <input
                className="tl-search-input"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button className="tl-search-clear" onClick={() => setQuery('')}>×</button>
              )}
            </div>
          )}
          {canWrite && (
            <button className="btn-primary" onClick={openCreate}>
              + Add
            </button>
          )}
        </div>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="empty-state-small">{emptyMessage}</div>
      ) : visibleRows.length === 0 ? (
        <div className="empty-state-small">No matches for “{query}”.</div>
      ) : (
        <div className="entity-table-wrap">
          <table className="entity-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
                {(canWrite || actions.length > 0) && <th style={{ width: 1 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.id}>
                  {columns.map((c) => (
                    <td key={c.key} title={!c.render && row[c.key] != null ? String(row[c.key]) : undefined}>
                      {c.render ? c.render(row) : row[c.key] ?? <span className="muted">—</span>}
                    </td>
                  ))}
                  {(canWrite || actions.length > 0) && (
                    <td className="row-actions">
                      {actions.map((a) => (
                        <button
                          key={a.label}
                          className="btn-link-sm"
                          onClick={() => a.onClick(row)}
                        >
                          {a.label}
                        </button>
                      ))}
                      {canWrite && (
                        <>
                          <button className="btn-link-sm" onClick={() => openEdit(row)}>
                            Edit
                          </button>
                          <button
                            className="btn-link-sm danger"
                            onClick={() => setDeleteTarget(row)}
                          >
                            {deleteLabel}
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EntityFormModal
        open={modalOpen}
        title={editing ? `Edit ${title.replace(/ches$/, 'ch').replace(/s$/, '').replace(/ie$/, 'y')}` : `Add ${title.replace(/ches$/, 'ch').replace(/s$/, '').replace(/ie$/, 'y')}`}
        fields={formFields}
        initial={editing}
        onSubmit={handleSubmit}
        onClose={() => setModalOpen(false)}
      />

      <ConfirmDeleteModal
        open={!!deleteTarget}
        title={deleteConfirmTitle || `${deleteLabel} this ${title.replace(/ches$/, 'ch').replace(/s$/, '').replace(/ie$/, 'y').toLowerCase()}?`}
        message={deleteConfirmMessage}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </section>
  );
}
